import React from 'react'
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  useTheme,
  alpha,
  Chip,
} from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Dashboard,
  Category,
  Group,
  CreditCard,
  Notifications,
  Person,
  AdminPanelSettings,
  TrendingUp,
  Receipt,
} from '@mui/icons-material'
import { usePermissions } from '../../contexts/PermissionContext'

const drawerWidth = 280

interface SidebarProps {
  open?: boolean
  onClose?: () => void
  variant?: 'permanent' | 'temporary'
}

const Sidebar: React.FC<SidebarProps> = ({ 
  open = true, 
  onClose, 
  variant = 'permanent' 
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const permissions = usePermissions()
  const theme = useTheme()

  const menuItems = [
    { 
      text: 'Dashboard', 
      icon: <Dashboard />, 
      path: '/dashboard',
      description: 'Overview & Analytics'
    },
    { 
      text: 'Transactions', 
      icon: <Receipt />, 
      path: '/transactions',
      description: 'Income & Expenses'
    },
    { 
      text: 'Categories', 
      icon: <Category />, 
      path: '/categories',
      description: 'Organize Spending'
    },
    { 
      text: 'Groups', 
      icon: <Group />, 
      path: '/groups',
      description: 'Shared Expenses'
    },
    { 
      text: 'Loans & EMI', 
      icon: <CreditCard />, 
      path: '/loans',
      description: 'Debt Management'
    },
    { 
      text: 'Reminders', 
      icon: <Notifications />, 
      path: '/reminders',
      description: 'Payment Alerts'
    },
  ]

  const accountItems = [
    { 
      text: 'Profile', 
      icon: <Person />, 
      path: '/profile',
      description: 'Account Settings'
    },
  ]

  // Add admin menu item if user has admin permissions
  if (permissions.isAdmin()) {
    accountItems.push({
      text: 'Admin Panel',
      icon: <AdminPanelSettings />,
      path: '/admin/permissions',
      description: 'System Management'
    })
  }

  const handleNavigation = (path: string) => {
    navigate(path)
    if (variant === 'temporary' && onClose) {
      onClose()
    }
  }

  const isSelected = (path: string) => location.pathname === path

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Fixed Header */}
      <Box 
        sx={{ 
          p: 3, 
          pb: 2,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1.5rem',
            }}
          >
            $
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              FinanceTracker
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Personal Finance Manager
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Scrollable Main Navigation */}
      <Box 
        sx={{ 
          flex: 1, 
          overflow: 'auto',
          py: 1,
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.text.secondary, 0.2),
            borderRadius: 3,
            '&:hover': {
              backgroundColor: alpha(theme.palette.text.secondary, 0.3),
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 1,
            }}
          >
            Main Menu
          </Typography>
        </Box>
        
        <List sx={{ px: 1 }}>
          {menuItems.map((item) => (
            <ListItem
              key={item.text}
              onClick={() => handleNavigation(item.path)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                backgroundColor: isSelected(item.path) 
                  ? alpha(theme.palette.primary.main, 0.1)
                  : 'transparent',
                '&:hover': {
                  backgroundColor: isSelected(item.path)
                    ? alpha(theme.palette.primary.main, 0.15)
                    : alpha(theme.palette.action.hover, 0.5),
                  transform: 'translateX(4px)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isSelected(item.path) 
                    ? 'primary.main' 
                    : 'text.secondary',
                  minWidth: 40,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isSelected(item.path) ? 600 : 500,
                      color: isSelected(item.path) 
                        ? 'primary.main' 
                        : 'text.primary',
                    }}
                  >
                    {item.text}
                  </Typography>
                }
                secondary={
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                    }}
                  >
                    {item.description}
                  </Typography>
                }
              />
              {isSelected(item.path) && (
                <Box
                  sx={{
                    width: 3,
                    height: 20,
                    backgroundColor: 'primary.main',
                    borderRadius: 1.5,
                  }}
                />
              )}
            </ListItem>
          ))}
        </List>

        <Divider sx={{ mx: 2, my: 2 }} />

        {/* Account Section */}
        <Box sx={{ px: 2, py: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 1,
            }}
          >
            Account
          </Typography>
        </Box>

        <List sx={{ px: 1 }}>
          {accountItems.map((item) => (
            <ListItem
              key={item.text}
              onClick={() => handleNavigation(item.path)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                backgroundColor: isSelected(item.path) 
                  ? alpha(theme.palette.primary.main, 0.1)
                  : 'transparent',
                '&:hover': {
                  backgroundColor: isSelected(item.path)
                    ? alpha(theme.palette.primary.main, 0.15)
                    : alpha(theme.palette.action.hover, 0.5),
                  transform: 'translateX(4px)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isSelected(item.path) 
                    ? 'primary.main' 
                    : 'text.secondary',
                  minWidth: 40,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isSelected(item.path) ? 600 : 500,
                        color: isSelected(item.path) 
                          ? 'primary.main' 
                          : 'text.primary',
                      }}
                    >
                      {item.text}
                    </Typography>
                    {item.text === 'Admin Panel' && (
                      <Chip
                        label="Admin"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 16, fontSize: '0.6rem' }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                    }}
                  >
                    {item.description}
                  </Typography>
                }
              />
              {isSelected(item.path) && (
                <Box
                  sx={{
                    width: 3,
                    height: 20,
                    backgroundColor: 'primary.main',
                    borderRadius: 1.5,
                  }}
                />
              )}
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Fixed Footer */}
      <Box 
        sx={{ 
          p: 2, 
          borderTop: 1, 
          borderColor: 'divider',
          flexShrink: 0,
          backgroundColor: 'background.paper',
        }}
      >
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            background: alpha(theme.palette.primary.main, 0.05),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TrendingUp sx={{ color: 'success.main', fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Monthly Progress
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            You're on track with your budget this month!
          </Typography>
        </Box>
      </Box>
    </Box>
  )

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          border: 'none',
          backgroundColor: 'background.paper',
          ...(variant === 'permanent' && {
            position: 'fixed',
            height: '100vh',
            left: 0,
            top: 0,
            zIndex: theme.zIndex.drawer,
            // Ensure sidebar stays fixed and doesn't scroll with content
            overflowY: 'auto',
            overflowX: 'hidden',
          }),
          ...(variant === 'temporary' && {
            height: '100vh',
          }),
        },
      }}
    >
      {drawerContent}
    </Drawer>
  )
}

export default Sidebar